import { DateFormatter, Translate } from '@components'

import { FormattedNumber, TableColumn } from '@billy/design-system'
import { Currency } from '@billy/design-system/src/types/currency'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { RecurringInvoiceStatus } from '@features/recurringInvoices/enums/recurringInvoiceStatus'
import { getRecurringInvoiceFrequencyLabel } from '@features/recurringInvoices/utils/getRecurringInvoiceFrequencyLabel'

import { RecurringInvoicesTableAccessor } from '../../../enums/recurringInvoicesTableAccessor'
import { RecurringInvoicesTableRow } from '../../../types/recurringInvoicesTableRow'
import { RecurringInvoiceStatusFormatter } from '../elements/RecurringInvoiceStatusFormatter'

const defaultColumns: RecurringInvoicesTableAccessor[] = [
  RecurringInvoicesTableAccessor.Status,
  RecurringInvoicesTableAccessor.Customer,
  RecurringInvoicesTableAccessor.Date,
  RecurringInvoicesTableAccessor.FirstInvoice,
  RecurringInvoicesTableAccessor.Frequency,
  RecurringInvoicesTableAccessor.NextInvoice,
  RecurringInvoicesTableAccessor.LastInvoice,
  RecurringInvoicesTableAccessor.GrossTotal,
]

export const useRecurringInvoicesTableColumns = () => {
  const { t } = useTranslation()

  const columns: TableColumn<RecurringInvoicesTableRow>[] = useMemo(() => {
    const mappedColumns: TableColumn<RecurringInvoicesTableRow>[] = defaultColumns.map((column) => {
      switch (column) {
        case RecurringInvoicesTableAccessor.Status:
          return {
            accessor: RecurringInvoicesTableAccessor.Status,
            alignment: 'center',
            Cell: ({ value: status }) => <RecurringInvoiceStatusFormatter status={status} />,
            Header: <Translate value="recurring_invoices.list.table.column.status.header" />,
            size: 'xxs',
            sortable: false,
          }

        case RecurringInvoicesTableAccessor.Customer:
          return {
            accessor: RecurringInvoicesTableAccessor.Customer,
            alignment: 'left',
            Cell: ({ value }) => value || '-',
            Header: <Translate value="recurring_invoices.list.table.column.customer.header" />,
            size: 's',
            sortable: false,
          }

        case RecurringInvoicesTableAccessor.Date:
          return {
            accessor: RecurringInvoicesTableAccessor.Date,
            Header: <Translate value="recurring_invoices.list.table.column.date.header" />,
            Cell: ({ value: date }) => <DateFormatter value={date} />,
            size: 's',
            sortable: true,
            truncate: true,
          }

        case RecurringInvoicesTableAccessor.FirstInvoice:
          return {
            accessor: RecurringInvoicesTableAccessor.FirstInvoice,
            Header: <Translate value="recurring_invoices.list.table.column.first_invoice.header" />,
            Cell: ({ row, value: startDate }) =>
              startDate && row.original.status !== RecurringInvoiceStatus.Draft ? (
                <DateFormatter value={startDate} />
              ) : (
                '-'
              ),
            size: 's',
            sortable: false,
            truncate: true,
          }

        case RecurringInvoicesTableAccessor.Frequency:
          return {
            accessor: RecurringInvoicesTableAccessor.Frequency,
            Header: <Translate value="recurring_invoices.list.table.column.frequency.header" />,
            Cell: ({ value: frequency }) => getRecurringInvoiceFrequencyLabel(frequency, t) || '-',
            size: 's',
            sortable: false,
            truncate: true,
          }

        case RecurringInvoicesTableAccessor.NextInvoice:
          return {
            accessor: RecurringInvoicesTableAccessor.NextInvoice,
            Header: <Translate value="recurring_invoices.list.table.column.next_invoice.header" />,
            Cell: ({ value: date }) => (date ? <DateFormatter value={date} /> : '-'),
            size: 's',
            sortable: false,
            truncate: true,
          }

        case RecurringInvoicesTableAccessor.LastInvoice:
          return {
            accessor: RecurringInvoicesTableAccessor.LastInvoice,
            Header: <Translate value="recurring_invoices.list.table.column.last_invoice.header" />,
            Cell: ({ value: date }) => (date ? <DateFormatter value={date} /> : '-'),
            size: 's',
            sortable: false,
            truncate: true,
          }

        case RecurringInvoicesTableAccessor.GrossTotal:
          return {
            accessor: RecurringInvoicesTableAccessor.GrossTotal,
            Header: <Translate value="recurring_invoices.list.table.column.total_gross.header" />,
            Cell: ({ row, value }) => (
              <FormattedNumber.Currency currency={row.original.currency as Currency} value={value} />
            ),
            alignment: 'right',
            size: 's',
            sortable: false,
            truncate: true,
          }

        default:
          throw new Error(`Unsupported column: ${column}`)
      }
    })

    return mappedColumns
  }, [t])

  return {
    columns,
  }
}
